import { axios, APIBASE_REVIVERAPPLICATION } from "./ApiConfig";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  AvailableDrivers,
  Collection,
  CollectionList,
  CollectionSlots,
  CollectionStatus,
  CollectionSuggestion,
  CreateTransportDevice,
  FullContainerQueue,
  TransportDevice,
  TransportDeviceType,
} from "@/types";

const getFullContainers = async () => axios.get(`${APIBASE_REVIVERAPPLICATION}api/collections/full-containers`);

export const useGetFullContainers = (options?: Partial<UseQueryOptions<FullContainerQueue>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<FullContainerQueue>({
    queryKey: ["getFullContainers"],
    queryFn: async () => {
      const result = await getFullContainers();
      return result.data;
    },
    ...queryOptions,
  });
};

const getCollectionSuggestion = async (containerId: number) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/collections/full-containers/${containerId}/suggested-slot`);

/**
 * Scheduling hint for a queued container. `slotBookingRequired` on the response decides whether the
 * site's slots must be booked or the arrival time is supplied by hand.
 */
export const useGetCollectionSuggestion = (
  containerId: number,
  options?: Partial<UseQueryOptions<CollectionSuggestion>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<CollectionSuggestion>({
    queryKey: ["collectionSuggestion", containerId],
    queryFn: async () => {
      const result = await getCollectionSuggestion(containerId);
      return result.data;
    },
    ...queryOptions,
  });
};

const getCollectionSlots = async (containerId: number, fromDate: string, toDate: string) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/collections/full-containers/${containerId}/slots`, {
    params: { fromDate, toDate },
  });

/**
 * Bookable arrival windows at the container's site, each with the drivers free to serve it — so no
 * separate driver call is needed in slot mode. A site with no slot configuration returns an empty
 * list, which is also how a non-slot site is detected.
 */
export const useGetCollectionSlots = (
  containerId: number,
  fromDate: string,
  toDate: string,
  options?: Partial<UseQueryOptions<CollectionSlots>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<CollectionSlots>({
    queryKey: ["collectionSlots", containerId, fromDate, toDate],
    queryFn: async () => {
      const result = await getCollectionSlots(containerId, fromDate, toDate);
      return result.data;
    },
    ...queryOptions,
  });
};

const getContainerDrivers = async (containerId: number, plannedDate: string) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/collections/full-containers/${containerId}/drivers`, {
    params: { plannedDate },
  });

/**
 * Drivers free for a container that has no collection yet, so the arrival window has to be supplied.
 * Used only by non-slot sites — in slot mode the drivers come with the slots.
 */
export const useGetContainerDrivers = (
  containerId: number,
  plannedDate: string,
  options?: Partial<UseQueryOptions<AvailableDrivers>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<AvailableDrivers>({
    queryKey: ["containerDrivers", containerId, plannedDate],
    queryFn: async () => {
      const result = await getContainerDrivers(containerId, plannedDate);
      return result.data;
    },
    ...queryOptions,
  });
};

const getCollectionDrivers = async (id: number, plannedDate?: string) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/collections/${id}/available-drivers`, {
    params: plannedDate ? { plannedDate } : undefined,
  });

/**
 * Drivers free for an existing collection. Without `plannedDate` it defaults to the collection's own
 * window and excludes the collection from the clash check, so the currently assigned driver is
 * included — that's the swap-driver case. With `plannedDate` it answers "who's free if I move it
 * here", which only applies to non-slot sites.
 */
export const useGetCollectionDrivers = (
  id: number,
  plannedDate?: string,
  options?: Partial<UseQueryOptions<AvailableDrivers>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<AvailableDrivers>({
    queryKey: ["collectionDrivers", id, plannedDate ?? null],
    queryFn: async () => {
      const result = await getCollectionDrivers(id, plannedDate);
      return result.data;
    },
    ...queryOptions,
  });
};

export const putReassignDriver = async (id: number, driverUserId: number) => {
  return axios.put<Collection>(`${APIBASE_REVIVERAPPLICATION}api/collections/${id}/assign-driver`, {
    driverUserId,
  });
};

/** Exactly one of `receptionSlotId` / `plannedDate`. Booking a slot releases the old one. */
export interface ChangePlannedDatePayload {
  receptionSlotId?: number;
  plannedDate?: string;
}

export const putUpdatePickupDate = async (id: number, payload: ChangePlannedDatePayload) => {
  return axios.put<Collection>(`${APIBASE_REVIVERAPPLICATION}api/collections/${id}/planned-date`, payload);
};

const getCollections = async (from: string, to: string, status?: CollectionStatus, sort?: string) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/collections`, {
    params: { from, to, status, sort },
  });

export const useGetCollections = (
  from: string,
  to: string,
  status?: CollectionStatus,
  sort?: string,
  options?: Partial<UseQueryOptions<CollectionList>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<CollectionList>({
    queryKey: ["getCollections", from, to, status, sort],
    queryFn: async () => {
      const result = await getCollections(from, to, status, sort);
      return result.data;
    },
    ...queryOptions,
  });
};

/** Exactly one of `receptionSlotId` / `plannedDate` — the slot booking and the collection in one call. */
export interface ScheduleCollectionPayload {
  containerId: number;
  driverUserId: number;
  receptionSlotId?: number;
  plannedDate?: string;
}

export const postScheduleCollection = async (payload: ScheduleCollectionPayload) => {
  return axios.post<Collection>(`${APIBASE_REVIVERAPPLICATION}api/collections`, payload);
};

const getTransportDeviceTypes = async () => axios.get(`${APIBASE_REVIVERAPPLICATION}api/transport-devices/types`);

export const useGetTransportDeviceTypes = (options?: Partial<UseQueryOptions<TransportDeviceType[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<TransportDeviceType[]>({
    queryKey: ["getTransportDeviceTypes"],
    queryFn: async () => {
      const result = await getTransportDeviceTypes();
      return result.data;
    },
    ...queryOptions,
  });
};

const getTransportDevices = async () => axios.get(`${APIBASE_REVIVERAPPLICATION}api/transport-devices`);

export const useGetTransportDevices = (options?: Partial<UseQueryOptions<TransportDevice[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<TransportDevice[]>({
    queryKey: ["getTransportDevices"],
    queryFn: async () => {
      const result = await getTransportDevices();
      return result.data;
    },
    ...queryOptions,
  });
};

const getTransportDevice = async (id: number) => axios.get(`${APIBASE_REVIVERAPPLICATION}api/transport-devices/${id}`);

export const useGetTransportDevice = (id: number, options?: Partial<UseQueryOptions<TransportDevice>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<TransportDevice>({
    queryKey: ["getTransportDevice", id],
    queryFn: async () => {
      const result = await getTransportDevice(id);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postCreateTransportDevice = async (data: CreateTransportDevice) => {
  return axios.post<TransportDevice>(`${APIBASE_REVIVERAPPLICATION}api/transport-devices`, data);
};

export const deleteTransportDevice = async (id: number) => {
  return axios.delete(`${APIBASE_REVIVERAPPLICATION}api/transport-devices/${id}`);
};
